<template lang="pug">
#facility-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 施設情報修正
    .box-body.register-body
      .note
        span 施設情報はご家族・施設関係者がLINEから見ることが出来ます。
      FormInput(
        key-name="施設名"
        required
        name="facility-name"
        v-model="name"
        type="text"
        placeholder="施設名"
        :maxlength="100"
        :validation-rules="{ required: true, max: 100 }")
      FormInput(
        key-name="住所"
        required
        name="facility-address"
        v-model="address"
        type="text"
        placeholder="住所"
        :maxlength="38"
        :validation-rules="{ required: true, max: 38 }")
      FormInput(
        key-name="電話番号"
        required
        name="facility-tel"
        v-model="tel"
        type="text"
        placeholder="電話番号"
        :maxlength="13"
        :validation-rules="{ required: true, max: 13, tel: true }")
      FormInput(
        key-name="ホームページ"
        name="facility-url"
        v-model="url"
        type="text"
        placeholder="ホームページ"
        :maxlength="10000"
        :validation-rules="{ url: { require_protocol: ['http', 'https'] }, max: 10000 }")
      FormFile(
        key-name="画像"
        required
        name="image-file"
        v-model="imageFile"
        :file-name.sync="imageName"
        mimetype="image/*"
        width="450px"
        :validationRules="{ required: true }"
        clip)
        template(v-slot:question)
          HelpIcon(:title="'選択可能なファイルについて'" :text="'jpegもしくはpngを選択可能です。'")
      FormImage(
        v-show="imageUrl"
        key-name="プレビュー"
        v-model="imageUrl")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoFacility" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="施設情報を更新しますか？" @ok="updateFacility")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoFacility")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Watch, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import FacilitiesService, { FacilityGetModel, FacilityPatchModel } from "@/commons/services/Facilities";
import S3UrlService from "@/commons/services/S3Url";
import PublicS3UrlToBase64 from "@/commons/utils/PublicS3UrlToBase64";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormFile from "@/pc/components/forms/FormFile.vue";
import FormImage from "@/pc/components/forms/FormImage.vue";

/**
 * 施設画面コンポーネント
 */
@Component({
  components: {
    HelpIcon,
    OkCancelModal,
    OkModal,
    ValidationAlert,
    FormInput,
    FormFile,
    FormImage,
    PageLoading
  },
  $_veeValidate: { validator: "new" }
})
export default class FacilityEdit extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 施設 */
  facility: FacilityGetModel | null = null;
  /** 施設名 */
  name: string = "";
  /** 住所 */
  address: string = "";
  /** 電話番号 */
  tel: string = "";
  /** HPのURL */
  url: string = "";
  /** ファイル選択コンポーネントで選択したファイル */
  imageFile: any = "";
  /** ファイル選択コンポーネントで選択したファイルのURL */
  imageUrl: string = "";
  /** ファイル選択コンポーネントで選択したファイル名 */
  imageName: string = "";
  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    await this.createView();

    this.isBusy = false;

    return;
  }

  /**
   * 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.facility = await this.getFacility();

    // 編集してAPIにPATCHメソッドで渡すので、取得した施設情報とは別定義のdataに格納する
    this.name = this.facility.name;
    this.address = this.facility.address;
    this.tel = this.facility.tel;
    this.url = this.facility.url;
    if (this.facility.imageUrl) {
      // 画像のURLの末尾がファイル名
      const imagePaths = this.facility.imageUrl.split("/");
      this.imageName = imagePaths[imagePaths.length - 1];
      // 画像URLが存在する場合は画像をBase64エンコードしたものを保持し、表示する
      const imageUrl: string = await this.convertImageUrl(this.facility.imageUrl);
      this.imageFile = await PublicS3UrlToBase64.convert(imageUrl, "image/jpeg");
      this.imageUrl = this.imageFile;
    }

    return;
  }

  /**
   * 施設取得
   */
  public async getFacility(): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore);
    return response;
  }

  /**
   * DBに保存されているS3URLを公開状態にし、公開状態にした画像のURLを返却する
   */
  public async convertImageUrl(imageUrl: string): Promise<string> {
    // 画像のURLがない場合は空を返す
    if (!imageUrl) {
      return "";
    }

    // 画像URL取得
    const convertImageUrl = await this.s3UrlService.get(userStore, Setting.backets.imageExpireHour, imageUrl);

    return convertImageUrl;
  }

  /**
   * FileSelecterコンポーネントにバインドした画像の変更イベント
   * ファイル選択、ファイル削除時に呼ばれる
   */
  @Watch("imageFile")
  public async changeImageFile(newFile: string): Promise<void> {
    if (newFile) {
      this.imageUrl = newFile;
    } else {
      this.imageUrl = "";
    }

    return;
  }

  /**
   * 登録確認モーダルを表示する
   * 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmUpdateModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * 施設更新
   */
  public async updateFacility(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const param: FacilityPatchModel = await this.createUpdateParam();
      await this.facilitiesService.patch(userStore, param);

      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * 施設更新パラメータ作成
   */
  public createUpdateParam(): FacilityPatchModel {
    const facility: FacilityGetModel = this.facility as FacilityGetModel;

    const facilityPatchModel: FacilityPatchModel = {
      name: this.$stringUtil.convertYen(this.name),
      address: this.$stringUtil.convertYen(this.address),
      tel: this.tel,
      url: this.url ? this.$stringUtil.convertYen(this.url) : "",
      imageUrl: this.imageFile,
      useAdmissionApplicationTemplateId: facility.useAdmissionApplicationTemplateId,
      useAdmissionJudgementTemplateId: facility.useAdmissionJudgementTemplateId,
      recruitmentRoom: facility.recruitmentRoom,
      updated: facility.updated
    };

    return facilityPatchModel;
  }

  /**
   * 施設画面に遷移する
   */
  public gotoFacility(): void {
    this.$router.push(PcUrl.Facility);

    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#facility-edit {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  .note {
    margin: 0 20px 20px 20px;
  }
  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
